/**
 * PlanUpgradeDialog variant config. Data only; no JSX. Icons resolved in PlanUpgradeDialog via ctaIconKey.
 *
 * @internal Used by PlanUpgradeDialog and GlobalDialogs.
 */

import type { TFunction } from 'i18next';

export type PlanUpgradeDialogVariant = 'usage_limit_exceeded' | 'plan_expired' | 'no_plan';

export type UsageMetric = 'cv' | 'interview';

export type PlanUpgradeDialogIconKey = 'sparkles' | 'refresh';

export interface UsageLimitConfig {
  variant: 'usage_limit_exceeded';
  metric: UsageMetric;
  limit: number;
  used: number;
}

export interface RenewalConfig {
  variant: 'plan_expired' | 'no_plan';
}

export type PlanUpgradeDialogConfig = UsageLimitConfig | RenewalConfig;

/** Result of getPlanUpgradeDialogProps; passed to PlanUpgradeDialogTemplate. */
export interface PlanUpgradeDialogPropsResult {
  title: string;
  description: string;
  ctaLabel: string;
  ctaIconKey: PlanUpgradeDialogIconKey;
  maxWidth: 'lg';
  progress?: { label: string; used: number; limit: number };
}


/** Maps config to template props; usage_limit_exceeded adds progress. */
export function getPlanUpgradeDialogProps(
  config: PlanUpgradeDialogConfig,
  t: TFunction<'dashboard'>,
): PlanUpgradeDialogPropsResult {
  const metricLabels: Record<UsageMetric, string> = {
    cv: t('plan_upgrade.metric_cv'),
    interview: t('plan_upgrade.metric_interview'),
  };
  const usageLimitDescriptions: Record<UsageMetric, string> = {
    cv: t('plan_upgrade.usage_cv_desc'),
    interview: t('plan_upgrade.usage_interview_desc'),
  };
  const variantConfig: Record<PlanUpgradeDialogVariant, Omit<PlanUpgradeDialogPropsResult, 'progress'>> = {
    usage_limit_exceeded: {
      title: t('plan_upgrade.usage_title'),
      description: '',
      ctaLabel: t('plan_upgrade.cta_upgrade'),
      ctaIconKey: 'sparkles',
      maxWidth: 'lg',
    },
    plan_expired: {
      title: t('plan_upgrade.expired_title'),
      description: t('plan_upgrade.expired_desc'),
      ctaLabel: t('plan_upgrade.cta_renew'),
      ctaIconKey: 'refresh',
      maxWidth: 'lg',
    },
    no_plan: {
      title: t('plan_upgrade.no_plan_title'),
      description: t('plan_upgrade.no_plan_desc'),
      ctaLabel: t('plan_upgrade.cta_get'),
      ctaIconKey: 'sparkles',
      maxWidth: 'lg',
    },
  };

  if (config.variant === 'usage_limit_exceeded') {
    return {
      ...variantConfig.usage_limit_exceeded,
      title: t('plan_upgrade.usage_metric_title', { metric: metricLabels[config.metric].toLowerCase() }),
      description: usageLimitDescriptions[config.metric],
      progress: {
        label: metricLabels[config.metric],
        used: config.used,
        limit: config.limit,
      },
    };
  }
  return variantConfig[config.variant];
}
